import type { List } from 'immutable';
import { useContext, useEffect } from 'react';

import { RouterState, RouterContext } from 'pkg/router/store';
import type { RouterStateType, ActionType } from 'pkg/router/store';
import { setConfirmNavigation } from 'pkg/router/utils';

interface RouterStateReturn {
	store: List<RouterStateType>;
	pushState: (location: string, state?: Record<string, unknown>) => void;
	popState: () => void;
	replaceState: (location: string, state?: Record<string, unknown>) => void;
}

export const useCurrentRouterState = (
	fallbackLocation = '/'
): [RouterStateType, string] => {
	const { store, stateType } = useContext(RouterContext);

	let currentState = new RouterState({
		location: fallbackLocation,
	});

	const history = store.get('history');

	if (history?.size >= 1) {
		currentState = history.last()!;
	}

	return [currentState, stateType];
};

export const useRouterState = (): RouterStateReturn => {
	const { store, dispatch, stateType } = useContext(RouterContext);
	const history = store.get('history');

	const pushState = (location: string, state: Record<string, unknown> = {}) => {
		return dispatch({
			type: `${stateType}/PUSH` as ActionType,
			payload: { location, state },
		});
	};

	const popState = () => {
		return dispatch({
			type: `${stateType}/POP` as ActionType,
		});
	};

	const replaceState = (
		location: string,
		state: Record<string, unknown> = {}
	) => {
		return dispatch({
			type: `${stateType}/REPLACE` as ActionType,
			payload: { location, state },
		});
	};

	return { store: history, pushState, popState, replaceState };
};

export const useRouterDispatch = () => {
	const { stateType, dispatch } = useContext(RouterContext);

	return (type: string, payload: Record<string, unknown> = {}) =>
		dispatch({
			type: `${stateType}/${type.toUpperCase()}` as ActionType,
			payload,
		});
};

export const useConfirmNavigation = (
	confirmNavigation: string | null = null
): void => {
	useEffect(() => {
		setConfirmNavigation(confirmNavigation);

		return () => setConfirmNavigation(null);
	});
};

export const usePreviousRoutePath = (): string => {
	const { store } = useContext(RouterContext);
	const history = store.get('history');

	if (history.size <= 1) {
		return '';
	}

	const backRoute = history.get(history.size - 2);

	if (!backRoute) {
		return '';
	}

	return backRoute.get('location');
};
